<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
            <crud-button
              v-if="$checkPermission('AGENCYPARAMETERS')"
              x-large
              color="light-blue lighten-2"
              @clicked="create()"
              icon="fa-plus"
              :tooltip="$t('global.crud.button.add')"
            />
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col sm="12" md="6" cols="12">
                  <v-text-field
                    v-model="filter.allFields"
                    :label="$t('global.datatable.search')"
                    append-icon="fas fa-search"
                    single-line
                  ></v-text-field>
                </v-col>
                <v-col sm="12" md="6" cols="12">
                  <v-autocomplete
                    v-model="filter.agencyCode"
                    :items="agenciesFilterItems"
                    :loading="agenciesFilter.isLoading"
                    :search-input.sync="agenciesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('agencyParameters.agency')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesFilter.isEdit = true"
                    @change="agenciesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-container>
            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel
              url="/agencyParameters"
              :filter="this.filter"
              :filterExport="filterExport"
            />
            <ImportExcel url="/agencyParameters" @done="search" />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.code + item.agencyCode">
                <td>
                  <!-- Edit record -->
                  <crud-button
                    v-if="$checkPermission('AGENCYPARAMETERS')"
                    x-small
                    color="orange"
                    icon="fa-pen"
                    :tooltip="$t('global.crud.buttons.edit')"
                    @clicked="edit(item.code, item.agencyCode)"
                  ></crud-button>

                  <!-- Delete record -->
                  <crud-button
                    v-if="
                      (item.deletionDate === undefined ||
                        item.deletionDate === null) &&
                        $checkPermission('AGENCYPARAMETERS')
                    "
                    x-small
                    color="red"
                    icon="fa-trash"
                    :tooltip="$t('global.crud.buttons.delete')"
                    @clicked="suspend(item.code, item.agencyCode)"
                  ></crud-button>
                </td>
                <td>{{ item.code }}</td>
                <td>
                  <v-tooltip right>
                    <template v-slot:activator="{ on }">
                      <span v-on="on">{{ item.agency.code }}</span>
                    </template>
                    <span>{{ item.agency.name }}</span>
                  </v-tooltip>
                </td>
                <td>{{ item.value }}</td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-dialog v-model="dialog" max-width="600" persistent no-click-animation>
      <validation-observer ref="obs" v-slot="{ invalid, validated, passes }">
        <v-card>
          <v-toolbar dark color="primary" dense flat>
            <v-toolbar-title class="white--text">
              {{ formTitle }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="close()">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="details-list">
            <v-form>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    v-if="!editMode"
                    :name="$t('agencyParameters.code')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.code"
                      :items="agencyParametersTypesItems"
                      :loading="agencyParametersTypes.isLoading"
                      :search-input.sync="agencyParametersTypes.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('agencyParameters.code')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      @focus="agencyParametersTypes.isEdit = true"
                      @change="agencyParametersTypes.isEdit = false"
                      :error-messages="errors"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                  <v-text-field
                    v-else
                    :label="$t('agencyParameters.code')"
                    v-model="item.code"
                    disabled
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    v-if="!editMode"
                    :name="$t('agencyParameters.agency')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.agencyCode"
                      :items="agenciesItems"
                      :loading="agencies.isLoading"
                      :search-input.sync="agencies.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('agencyParameters.agency')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      @focus="agencies.isEdit = true"
                      @change="agencies.isEdit = false"
                      :error-messages="errors"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                  <v-text-field
                    v-else
                    :label="$t('agencyParameters.agency')"
                    v-model="item.agencyString"
                    disabled
                  ></v-text-field>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('agencyParameters.value')"
                    rules="required|max:100"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('agencyParameters.value')"
                      v-model="item.value"
                      required
                      :error-messages="errors"
                      counter="100"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click.native="close()">
              {{ $t("global.buttons.close") }}
            </v-btn>
            <v-btn v-if="editMode" color="orange" text @click="passes(update)">
              {{ $t("global.buttons.update") }}
            </v-btn>
            <v-btn v-else color="green" text @click="passes(save)">
              {{ $t("global.buttons.create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import ImportExcel from "@/components/ImportExcel.vue";

const CONTEXT = "/agencyParameters";
const CONTEXT_AGENCIES = "/agencies";
const CONTEXT_AGENCY_PARAMETERS_TYPES = "/agencyParametersTypes";
const ENTITY_NAME = "agencyParameters.";

export default {
  components: {
    CrudButton,
    ExportExcel,
    ImportExcel
  },
  name: "AgencyParameter",
  data: () => ({
    loading: false,
    searchTableWaiting: false,
    editMode: false,
    dialog: false,
    item: {},
    items: [],
    filter: {
      allFields: "",
      agencyCode: "",
      deletedItems: "ALL"
    },
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40],
    agencyParametersTypes: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agencies: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    }
  }),
  computed: {
    deletedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: "ALL" },
        { text: this.$t("global.status.active"), value: "ACTIVE" },
        { text: this.$t("global.status.deleted"), value: "DELETED" }
      ];
    },
    formTitle() {
      return this.editMode
        ? this.$t("agencyParameters.title.edit")
        : this.$t("agencyParameters.title.new");
    },
    headers() {
      return [
        {
          text: this.$t("global.datatable.header.actions"),
          value: "actions",
          sortable: false
        },
        {
          text: this.$t("agencyParameters.datatable.code"),
          value: ENTITY_NAME + "code"
        },
        {
          text: this.$t("agencyParameters.datatable.agency"),
          value: ENTITY_NAME + "agencyCode"
        },
        {
          text: this.$t("agencyParameters.datatable.value"),
          value: ENTITY_NAME + "value"
        }
      ];
    },
    agencyParametersTypesItems() {
      return this.agencyParametersTypes.entries.map(entry => {
        const description = entry.code + " - " + entry.description;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesItems() {
      return this.agencies.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesFilterItems() {
      return this.agenciesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.allFields": function() {
      if (!this.searchTableWaiting) {
        setTimeout(() => {
          this.search();
          this.searchTableWaiting = false;
        }, 500);
      }
      this.searchTableWaiting = true;
    },
    "agencyParametersTypes.search": function(value) {
      this.preLoadGenericSearchEntity(
        value,
        this.agencyParametersTypes,
        CONTEXT_AGENCY_PARAMETERS_TYPES
      );
    },
    "agencies.search": function(value) {
      this.preLoadGenericSearchEntity(value, this.agencies, CONTEXT_AGENCIES);
    },
    "agenciesFilter.search": function(value) {
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesFilter,
        CONTEXT_AGENCIES
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    preLoadGenericSearchEntity(value, object, context, aditionalFilter) {
      if (!object.isLoadingWaiting) {
        setTimeout(() => {
          this.genericSearchEntity(object, context, aditionalFilter);
          object.isLoadingWaiting = false;
        }, 500);
      }
      object.value = value;
      object.isLoadingWaiting = true;
    },
    async genericSearchEntity(object, context, aditionalFilter) {
      console.debug("genericSearchEntity");
      if (!object.isEdit || object.value === null || object.value === "")
        return;

      if (object.isLoading) return;

      object.isLoading = true;

      let filterGeneric = {
        allFields: object.value
      };
      if (aditionalFilter !== undefined) {
        Object.keys(aditionalFilter).forEach(
          key => (filterGeneric[key] = aditionalFilter[key])
        );
      }

      // Lazily load input items
      localhttp
        .post(context + "/allFilter", filterGeneric)
        .then(response => {
          console.debug(response.data);
          object.entries = response.data;
        })
        .catch(e => {
          console.log(e);
          this.$showError();
        })
        .finally(() => (object.isLoading = false));
    },
    create() {
      console.debug("Create element");
      this.editMode = false;
      this.resetDialog();
      this.dialog = true;
    },
    async save() {
      console.debug("Save form");
      localhttp
        .post(CONTEXT + "/create", this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    edit(id, agency) {
      console.debug("Edit element " + id);
      this.editMode = true;
      this.resetDialog();
      localhttp
        .get(CONTEXT + "/" + agency + "/" + id)
        .then(response => {
          this.item = response.data;
          this.editPrepare(this.item);
          this.dialog = true;
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    editPrepare(item) {
      item.agencyString = item.agency.code + " - " + item.agency.name;
    },
    async update() {
      console.debug("Update form");
      localhttp
        .put(
          CONTEXT + "/update/" + this.item.agencyCode + "/" + this.item.code,
          this.item
        )
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    async suspend(id, agency) {
      console.debug("Suspend element " + id);
      if (
        await this.$root.$confirm(
          this.$t("global.confirm"),
          this.$t("global.action.suspend.confirm"),
          { color: "blue" }
        )
      ) {
        localhttp
          .put(CONTEXT + "/delete/" + agency + "/" + id)
          .then(response => {
            console.log(response.data);
            this.search();
          })
          .catch(e => {
            console.log(e);
            this.$showErrorCode(e.response.status);
          });
      }
    },
    restore(id, agency) {
      console.debug("Restore element " + id);
      localhttp
        .put(CONTEXT + "/activate/" + agency + "/" + id)
        .then(response => {
          console.log(response.data);
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    refreshItemsView() {
      console.debug("Refresh table");
      this.search();
    },
    close() {
      this.dialog = false;
    },
    resetDialog() {
      this.item = {};
      this.resetAgenciesSearch();
      this.resetAgencyParametersTypesSearch();
      requestAnimationFrame(() => {
        if (this.$refs.obs !== undefined) {
          this.$refs.obs.reset();
        }
      });
    },
    resetAgencyParametersTypesSearch() {
      this.agencyParametersTypes.search = "";
      this.agencyParametersTypes.entries = [];
    },
    resetAgenciesSearch() {
      this.agencies.search = "";
      this.agencies.entries = [];
    },
    filterExport(jsonData) {
      jsonData.forEach(function(v) {
        delete v.agency;
      });
    }
  }
};
</script>
